import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CircleCheck, Clock, FileQuestion, FileText, UserPlus, Users } from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  NavigationLink,
  SegmentedControl,
  Skeleton,
  StatCard,
} from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { membersAreaListQuery } from '../model/members-area.queries'
import { membersListQuery } from '../model/members-access.queries'
import type { MembersAreaListItem } from '../model/members-area.contract'
import type { MembersListItem } from '../model/members-access.contract'
import { ContentEditor } from './ContentEditor'
import { ContentsPanel } from './ContentsPanel'
import { CourtesyDrawer } from './CourtesyDrawer'
import { StudentDetailDrawer } from './StudentDetailDrawer'
import { StudentsPanel } from './StudentsPanel'
import {
  kpiGrid,
  kpiValue,
  muted,
  page,
  pageHeader,
  pageSubtitle,
  pageTitle,
  sectionCard,
  sectionHead,
  sectionTitle,
  tabsBar,
} from './members-area.css'

type AreaTab = 'contents' | 'members'

export function MembersAreaScreen() {
  const enabled = useFeatureFlag('areaDeMembros')
  const list = useQuery({ ...membersAreaListQuery(), enabled })
  const members = useQuery({ ...membersListQuery(), enabled })
  const [tab, setTab] = useState<AreaTab>('contents')
  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const [courtesyOpen, setCourtesyOpen] = useState(false)

  const pending = list.isPending || members.isPending
  const failed = list.isError || members.isError

  if (!enabled) {
    return (
      <EmptyState
        data-testid="members-area-unavailable"
        icon={<FileQuestion />}
        title="Página indisponível"
        description="Esta página não está disponível."
      />
    )
  }

  return (
    <div className={page} data-testid="members-area-screen">
      <header className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Área de membros</h1>
          <p className={pageSubtitle}>
            Complete o conteúdo de cada produto com a área de membros ligada e acompanhe quem tem
            acesso.
          </p>
        </div>
      </header>

      {pending ? (
        <div role="status" aria-label="Carregando área de membros">
          <div className={kpiGrid}>
            <Skeleton height={112} radius="16px" />
            <Skeleton height={112} radius="16px" />
            <Skeleton height={112} radius="16px" />
            <Skeleton height={112} radius="16px" />
          </div>
          <Skeleton height={360} radius="16px" />
        </div>
      ) : failed ? (
        <ErrorState
          title="Não foi possível carregar a área de membros"
          description="Tente novamente em instantes."
          onRetry={() => {
            void list.refetch()
            void members.refetch()
          }}
        />
      ) : (
        <MembersAreaContent
          tab={tab}
          onTabChange={setTab}
          items={list.data ?? []}
          members={members.data?.items ?? []}
          activeMembers={members.data?.activeCount ?? 0}
          onOpenContent={(item) => setEditingProductId(item.productId)}
          onOpenMember={(item) => setSelectedMemberId(item.id)}
          onAddCourtesy={() => setCourtesyOpen(true)}
        />
      )}

      <ContentEditor
        key={editingProductId ?? 'closed'}
        productId={editingProductId}
        open={editingProductId !== null}
        onClose={() => setEditingProductId(null)}
      />
      <StudentDetailDrawer memberId={selectedMemberId} onClose={() => setSelectedMemberId(null)} />
      <CourtesyDrawer
        open={courtesyOpen}
        products={(list.data ?? []).map((item) => ({ productId: item.productId, name: item.name }))}
        onClose={() => setCourtesyOpen(false)}
      />
    </div>
  )
}

function MembersAreaContent({
  tab,
  onTabChange,
  items,
  members,
  activeMembers,
  onOpenContent,
  onOpenMember,
  onAddCourtesy,
}: {
  tab: AreaTab
  onTabChange: (tab: AreaTab) => void
  items: MembersAreaListItem[]
  members: MembersListItem[]
  activeMembers: number
  onOpenContent: (item: MembersAreaListItem) => void
  onOpenMember: (item: MembersListItem) => void
  onAddCourtesy: () => void
}) {
  const published = items.filter((item) => item.status === 'PUBLISHED').length
  const pending = items.length - published

  return (
    <>
      <div className={kpiGrid} data-testid="members-kpis">
        <StatCard
          label="Produtos na área"
          icon={<FileText size={16} />}
          value={<span className={kpiValue}>{items.length}</span>}
          helper="Com a área de membros ligada"
        />
        <StatCard
          label="Publicados"
          icon={<CircleCheck size={16} />}
          value={<span className={kpiValue}>{published}</span>}
          helper="Visíveis para quem tiver acesso"
        />
        <StatCard
          label="Pendentes"
          icon={<Clock size={16} />}
          value={<span className={kpiValue}>{pending}</span>}
          helper="Rascunho ou cadastro incompleto"
        />
        <StatCard
          label="Membros ativos"
          icon={<Users size={16} />}
          value={<span className={kpiValue}>{activeMembers}</span>}
          helper="Com ao menos uma concessão ativa"
        />
      </div>

      <Card padding="md" className={sectionCard}>
        <div className={sectionHead}>
          <div className={tabsBar}>
            <SegmentedControl
              ariaLabel="Seções da área de membros"
              value={tab}
              onChange={onTabChange}
              options={[
                { value: 'contents', label: 'Conteúdos' },
                { value: 'members', label: 'Membros' },
              ]}
            />
            {tab === 'members' ? (
              <Button leftIcon={<UserPlus size={17} />} onClick={onAddCourtesy}>
                Adicionar cortesia
              </Button>
            ) : null}
          </div>
        </div>
        {tab === 'contents' ? (
          items.length === 0 ? (
            <EmptyState
              data-testid="members-contents-empty"
              icon={<FileText size={22} />}
              title="Nenhum produto na área de membros"
              description="Ligue o recurso Área de membros no cadastro do produto. Depois, volte aqui para completar o conteúdo."
              action={<NavigationLink href="/produtos">Ir para produtos</NavigationLink>}
            />
          ) : (
            <>
              <div>
                <h2 className={sectionTitle}>Produtos</h2>
                <p className={muted}>
                  Só entram aqui os produtos com o recurso Área de membros ligado.
                </p>
              </div>
              <ContentsPanel items={items} onOpen={onOpenContent} />
            </>
          )
        ) : (
          <StudentsPanel students={members} onAdd={onAddCourtesy} onOpen={onOpenMember} />
        )}
      </Card>
    </>
  )
}
